/**
 * Comment submission request
 */
export interface CommentRequest {
  projectId: string;
  articleId: string;
  content: string;
  parentId?: string;
}

/**
 * Reaction submission request
 */
export interface ReactionRequest {
  targetType: 'article' | 'comment';
  targetId: string;
  type: 'like' | 'dislike' | 'heart' | 'laugh' | 'angry';
}

/**
 * Article creation request
 */
export interface ArticleRequest {
  url: string;
  title?: string;
  projectId?: string;
}

/**
 * Comment update request
 */
export interface CommentUpdateRequest {
  content: string;
}

/**
 * Simple API service for widget
 */
export class WidgetApiService {
  private baseUrl: string;
  private token: string | null = null;
  private provider: string | null = null;
  private userEmail: string | null = null;
  private userName: string | null = null;
  private userAvatar: string | null = null;
  private onUnauthorizedCallback: (() => void) | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  /**
   * Set callback for handling 401 Unauthorized responses
   */
  setOnUnauthorized(callback: () => void): void {
    this.onUnauthorizedCallback = callback;
  }

  /**
   * Handle HTTP response and check for authentication errors
   */
  private async handleResponse(response: Response): Promise<any> {
    if (response.status === 401) {
      // Token expired or invalid - trigger logout
      if (this.onUnauthorizedCallback) {
        this.onUnauthorizedCallback();
      }
      throw new Error('Authentication expired. Please log in again.');
    }

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Set authentication token and user data
   */
  setToken(token: string, provider?: string, email?: string, name?: string, avatar?: string): void {
    this.token = token;
    this.provider = provider || null;
    this.userEmail = email || null;
    this.userName = name || null;
    this.userAvatar = avatar || null;
  }

  /**
   * Set user provider
   */
  setProvider(provider: string): void {
    this.provider = provider;
  }

  /**
   * Clear authentication token and provider
   */
  clearToken(): void {
    this.token = null;
    this.provider = null;
  }

  /**
   * Create or get article
   */
  async createOrGetArticle(request: ArticleRequest): Promise<any> {
    const url = `${this.baseUrl}/api/widget/articles`;
    
    const headers = {
      'Content-Type': 'application/json',
      ...(this.token && { 'Authorization': `Bearer ${this.token}` }),
    };
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    return this.handleResponse(response);
  }

  /**
   * Get comments for an article with mandatory project ID for security
   */
  async getComments(articleUrl: string, projectId: string): Promise<any> {
    if (!projectId) {
      throw new Error('Project ID is required for security isolation');
    }

    const url = `${this.baseUrl}/api/widget/comments?url=${encodeURIComponent(articleUrl)}&projectId=${encodeURIComponent(projectId)}`;
    
    const headers = {
      'Content-Type': 'application/json',
      ...(this.token && { 'Authorization': `Bearer ${this.token}` }),
    };
    
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    return this.handleResponse(response);
  }

  /**
   * Create a new comment
   */
  async createComment(request: CommentRequest): Promise<any> {
    if (!this.token) {
      throw new Error('Authentication required');
    }

    if (!this.provider) {
      throw new Error('User provider required');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.token}`,
    };

    if (this.provider) headers['X-User-Provider'] = this.provider;
    if (this.userEmail) headers['X-User-Email'] = this.userEmail;
    if (this.userName) headers['X-User-Name'] = this.userName;
    if (this.userAvatar) headers['X-User-Avatar'] = this.userAvatar;

    const response = await fetch(`${this.baseUrl}/api/widget/comments`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    return this.handleResponse(response);
  }

  /**
   * Update a comment with project ID validation
   */
  async updateComment(commentId: string, request: CommentUpdateRequest, projectId: string): Promise<any> {
    if (!this.token) {
      throw new Error('Authentication required');
    }

    if (!projectId) {
      throw new Error('Project ID is required for security isolation');
    }

    const response = await fetch(`${this.baseUrl}/api/widget/comments/${commentId}?projectId=${encodeURIComponent(projectId)}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`,
      },
      body: JSON.stringify(request),
    });

    return this.handleResponse(response);
  }

  /**
   * Delete a comment with project ID validation
   */
  async deleteComment(commentId: string, projectId: string): Promise<void> {
    if (!this.token) {
      throw new Error('Authentication required');
    }

    if (!projectId) {
      throw new Error('Project ID is required for security isolation');
    }

    const response = await fetch(`${this.baseUrl}/api/widget/comments/${commentId}?projectId=${encodeURIComponent(projectId)}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${this.token}`,
      },
    });

    if (response.status === 401) {
      if (this.onUnauthorizedCallback) {
        this.onUnauthorizedCallback();
      }
      throw new Error('Authentication expired. Please log in again.');
    }

    if (!response.ok) {
      throw new Error(`Failed to delete comment: ${response.statusText}`);
    }
  }

  /**
   * Toggle a reaction on a comment with project ID validation
   */
  async toggleReaction(commentId: string, reactionType: string, projectId: string): Promise<void> {
    if (!this.token) {
      throw new Error('Authentication required');
    }

    if (!projectId) {
      throw new Error('Project ID is required for security isolation');
    }

    const response = await fetch(`${this.baseUrl}/api/widget/comments/${commentId}/reactions?projectId=${encodeURIComponent(projectId)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`,
      },
      body: JSON.stringify({ type: reactionType }),
    });

    if (!response.ok) {
      throw new Error(`Failed to toggle reaction: ${response.statusText}`);
    }
  }

  /**
   * Submit a comment (requires authentication)
   */
  async submitComment(request: CommentRequest): Promise<void> {
    return this.createComment(request);
  }

  /**
   * Submit a reaction (requires authentication)
   */
  async submitReaction(request: ReactionRequest): Promise<void> {
    if (!this.token) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${this.baseUrl}/api/widget/reactions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`Failed to submit reaction: ${response.statusText}`);
    }
  }

  /**
   * Remove a reaction (requires authentication) with project ID validation
   */
  async removeReaction(request: ReactionRequest, projectId: string): Promise<void> {
    if (!this.token) {
      throw new Error('Authentication required');
    }

    if (!projectId) {
      throw new Error('Project ID is required for security isolation');
    }

    const response = await fetch(`${this.baseUrl}/api/widget/reactions?projectId=${encodeURIComponent(projectId)}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`Failed to remove reaction: ${response.statusText}`);
    }
  }

  /**
   * Get article reactions by article ID with project ID validation
   */
  async getArticleReactions(articleId: string, projectId: string): Promise<any> {
    if (!projectId) {
      throw new Error('Project ID is required for security isolation');
    }

    const url = `${this.baseUrl}/api/widget/articles/${articleId}/reactions?projectId=${encodeURIComponent(projectId)}`;
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }
    
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to get article reactions: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Toggle a reaction on an article (requires authentication)
   * This will add the reaction if it doesn't exist, or remove it if it does
   */
  async toggleArticleReaction(articleId: string, reactionType: string): Promise<any> {
    if (!this.token) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${this.baseUrl}/api/reactions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`,
      },
      body: JSON.stringify({
        articleId: articleId,
        type: reactionType
      }),
    });

    if (response.status === 401) {
      if (this.onUnauthorizedCallback) {
        this.onUnauthorizedCallback();
      }
      throw new Error('Authentication expired. Please log in again.');
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to toggle article reaction: ${response.statusText} - ${errorText}`);
    }

    return response.json();
  }

  /**
   * Submit a star rating for an article (requires authentication)
   * Star rating is 1-5 stars. Submitting a new rating will replace the user's previous rating.
   * @param articleId - The article ID
   * @param starValue - Star rating value (1-5)
   * @returns Promise with the reaction response
   */
  async submitStarRating(articleId: string, starValue: number): Promise<any> {
    if (!this.token) {
      throw new Error('Authentication required');
    }

    if (starValue < 1 || starValue > 5) {
      throw new Error('Star rating must be between 1 and 5');
    }

    // Map star value to ReactionType enum: Star1 = 101, Star2 = 102, etc.
    const reactionType = `Star${starValue}`;

    const response = await fetch(`${this.baseUrl}/api/reactions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`,
      },
      body: JSON.stringify({
        articleId: articleId,
        type: reactionType
      }),
    });

    if (response.status === 401) {
      if (this.onUnauthorizedCallback) {
        this.onUnauthorizedCallback();
      }
      throw new Error('Authentication expired. Please log in again.');
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to submit star rating: ${response.statusText} - ${errorText}`);
    }

    return response.json();
  }

  /**
   * Delete a star rating for an article (requires authentication)
   * Removes the user's star rating from the article.
   * @param articleId - The article ID
   * @param starValue - Star rating value to delete (1-5)
   * @param projectId - The project ID for security isolation
   * @returns Promise with the deletion response
   */
  async deleteStarRating(articleId: string, starValue: number, projectId: string): Promise<any> {
    if (!this.token) {
      throw new Error('Authentication required');
    }

    if (starValue < 1 || starValue > 5) {
      throw new Error('Star rating must be between 1 and 5');
    }

    // Map star value to ReactionType enum: Star1 = 101, Star2 = 102, etc.
    const reactionType = `Star${starValue}`;

    const response = await fetch(`${this.baseUrl}/api/reactions?projectId=${projectId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`,
        'X-User-Provider': this.provider || '',
      },
      body: JSON.stringify({
        targetType: 'Article',
        targetId: articleId,
        type: reactionType
      }),
    });

    if (response.status === 401) {
      if (this.onUnauthorizedCallback) {
        this.onUnauthorizedCallback();
      }
      throw new Error('Authentication expired. Please log in again.');
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to delete star rating: ${response.statusText} - ${errorText}`);
    }

    return response.json();
  }

  /**
   * Get star rating statistics for a comment
   * @param commentId - The comment ID
   * @param projectId - The project ID for security isolation
   * @returns Promise with star rating statistics including average, count, distribution, and user rating
   */
  async getCommentStarRating(commentId: string, projectId: string): Promise<any> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Include auth token if available
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(
      `${this.baseUrl}/api/widget/comments/${commentId}/star-rating?projectId=${projectId}`,
      {
        method: 'GET',
        headers: headers,
      }
    );

    if (response.status === 401 && this.onUnauthorizedCallback) {
      this.onUnauthorizedCallback();
      throw new Error('Authentication expired. Please log in again.');
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get comment star rating: ${response.statusText} - ${errorText}`);
    }

    return response.json();
  }

  /**
   * Submit a star rating for a comment (requires authentication)
   * Star rating is 1-5 stars. Submitting a new rating will replace the user's previous rating.
   * @param commentId - The comment ID
   * @param starValue - Star rating value (1-5)
   * @param projectId - The project ID for security isolation
   * @returns Promise with the reaction response
   */
  async submitCommentStarRating(commentId: string, starValue: number, projectId: string): Promise<any> {
    if (!this.token) {
      throw new Error('Authentication required');
    }

    if (starValue < 1 || starValue > 5) {
      throw new Error('Star rating must be between 1 and 5');
    }

    const response = await fetch(
      `${this.baseUrl}/api/widget/comments/${commentId}/star-rating?projectId=${projectId}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.token}`,
        },
        body: JSON.stringify({
          starRating: starValue
        }),
      }
    );

    if (response.status === 401) {
      if (this.onUnauthorizedCallback) {
        this.onUnauthorizedCallback();
      }
      throw new Error('Authentication expired. Please log in again.');
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to submit comment star rating: ${response.statusText} - ${errorText}`);
    }

    return response.json();
  }

  /**
   * Delete a star rating for a comment (requires authentication)
   * Removes the user's star rating from the comment.
   * @param commentId - The comment ID
   * @param projectId - The project ID for security isolation
   * @returns Promise with the deletion response
   */
  async deleteCommentStarRating(commentId: string, projectId: string): Promise<any> {
    if (!this.token) {
      throw new Error('Authentication required');
    }

    const response = await fetch(
      `${this.baseUrl}/api/widget/comments/${commentId}/star-rating?projectId=${projectId}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.token}`,
        },
      }
    );

    if (response.status === 401) {
      if (this.onUnauthorizedCallback) {
        this.onUnauthorizedCallback();
      }
      throw new Error('Authentication expired. Please log in again.');
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to delete comment star rating: ${response.statusText} - ${errorText}`);
    }

    return response.json();
  }
}